const HEX = /^#([0-9a-fA-F]{6})$/;

export const VENTURU_BRAND_PRIMARY = "#0A1628";
export const VENTURU_BRAND_ACCENT = "#00C853";
export const VENTURU_LEAD_AD_GREEN = "#20B256";

export function parseHexColor(
  value: string,
): { r: number; g: number; b: number } | null {
  const match = HEX.exec(value.trim());
  if (!match?.[1]) return null;
  const hex = match[1];
  return {
    r: Number.parseInt(hex.slice(0, 2), 16),
    g: Number.parseInt(hex.slice(2, 4), 16),
    b: Number.parseInt(hex.slice(4, 6), 16),
  };
}

function channelLuminance(channel: number): number {
  const scaled = channel / 255;
  return scaled <= 0.03928
    ? scaled / 12.92
    : ((scaled + 0.055) / 1.055) ** 2.4;
}

export function relativeLuminance(hex: string): number | null {
  const rgb = parseHexColor(hex);
  if (!rgb) return null;
  return (
    0.2126 * channelLuminance(rgb.r) +
    0.7152 * channelLuminance(rgb.g) +
    0.0722 * channelLuminance(rgb.b)
  );
}

export function contrastRatio(
  foreground: string,
  background: string,
): number | null {
  const left = relativeLuminance(foreground);
  const right = relativeLuminance(background);
  if (left == null || right == null) return null;
  const lighter = Math.max(left, right);
  const darker = Math.min(left, right);
  return (lighter + 0.05) / (darker + 0.05);
}

/** White or near-black, whichever reads better on the given background. */
export function readableTextColor(background: string): "#FFFFFF" | "#111111" {
  const white = contrastRatio("#FFFFFF", background);
  const black = contrastRatio("#111111", background);
  if (white == null || black == null) return "#FFFFFF";
  return white >= black ? "#FFFFFF" : "#111111";
}

/**
 * Tick color for a filled check circle. White on Venturu green and other
 * saturated colors; near-black only when the circle itself is light.
 */
export function readableMarkColor(background: string): "#FFFFFF" | "#111111" {
  const luminance = relativeLuminance(background);
  if (luminance != null && luminance > 0.62) return "#111111";
  return "#FFFFFF";
}

/** Check circles stay Venturu green unless a campaign sets its own color. */
export function resolveLeadAdCheckColor(checkColor?: string | null): string {
  if (checkColor && parseHexColor(checkColor)) return checkColor;
  return VENTURU_LEAD_AD_GREEN;
}

/** Uses the explicit CTA text color when valid, otherwise the best contrast. */
export function resolveLeadAdCtaTextColor(
  accentColor: string,
  ctaTextColor?: string | null,
): string {
  if (ctaTextColor && parseHexColor(ctaTextColor)) return ctaTextColor;
  return readableTextColor(accentColor);
}

/**
 * Lead ads stay Venturu green until an organization picks a different accent,
 * or a campaign sets its own color.
 */
export function resolveLeadAdAccent(
  themeAccent: string | null | undefined,
  override?: string | null,
  options?: { followOrganization?: boolean },
): string {
  if (override && parseHexColor(override)) return override;
  if (options?.followOrganization === false) return VENTURU_LEAD_AD_GREEN;
  if (
    themeAccent &&
    parseHexColor(themeAccent) &&
    themeAccent.toUpperCase() !== VENTURU_BRAND_ACCENT.toUpperCase()
  ) {
    return themeAccent;
  }
  return VENTURU_LEAD_AD_GREEN;
}
